import React, {memo, useEffect} from "react";
import {motion} from "framer-motion";
import {FaAddressBook, FaBatteryFull, FaChargingStation, FaImages, FaWifi,} from "react-icons/fa";

// Add TypeScript interface for feature items
interface FeatureItemProps {
    icon: React.ReactNode;
    text: string;
}

// Feature data object
const FEATURES: FeatureItemProps[] = [
    {
        icon: <FaImages className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Remove duplicate photos & clean storage",
    },
    {
        icon: (
            <FaAddressBook className="text-blue-600 dark:text-blue-400 w-5 h-5"/>
        ),
        text: "Merge duplicate contacts automatically",
    },
    {
        icon: (
            <FaChargingStation className="text-blue-600 dark:text-blue-400 w-5 h-5"/>
        ),
        text: "Visualize charging status with animations",
    },
    {
        icon: <FaWifi className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Monitor & optimize network speed",
    },
    {
        icon: (
            <FaBatteryFull className="text-blue-600 dark:text-blue-400 w-5 h-5"/>
        ),
        text: "Extend battery life with power optimization",
    },
];

// Animation variants
const containerVariants = {
    hidden: {opacity: 0},
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
        },
    },
};

const Hero = () => {
    useEffect(() => {
        // Set theme color for mobile browsers once on mount
        if (!document.querySelector('meta[name="theme-color"]')) {
            const metaThemeColor = document.createElement("meta");
            metaThemeColor.name = "theme-color";
            metaThemeColor.content = "#1e40af"; // blue-800
            document.head.appendChild(metaThemeColor);
        }
    }, []);

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            {/* Background and decorative elements */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 dark:from-blue-950 dark:via-blue-900 dark:to-indigo-950"/>
            <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

            {/* Floating decorative elements */}
            <FloatingElements/>

            {/* Header with logo and download button */}
            <Header/>

            {/* Main content */}
            <main className="flex-grow px-6 pt-8 pb-16 md:pt-12 md:pb-20 relative z-10">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto"
                >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12 lg:gap-20">
                        {/* Left column - content */}
                        <ContentSection features={FEATURES}/>

                        {/* Right column - device mockup */}
                        <DeviceMockup/>
                    </div>
                </motion.div>
            </main>

            {/* Footer */}
            <Footer/>
        </div>
    );
};

// Component for the floating decorative elements
const FloatingElements = memo(() => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
            className="absolute w-64 h-64 rounded-full bg-blue-400/10 dark:bg-blue-600/10"
            style={{top: "15%", left: "5%"}}
            animate={{y: [0, 15, 0], x: [0, 10, 0]}}
            transition={{repeat: Infinity, duration: 8, ease: "easeInOut"}}
        />
        <motion.div
            className="absolute w-32 h-32 rounded-full bg-indigo-400/10 dark:bg-indigo-600/10"
            style={{top: "60%", right: "10%"}}
            animate={{y: [0, -20, 0], x: [0, -5, 0]}}
            transition={{repeat: Infinity, duration: 10, ease: "easeInOut"}}
        />
    </div>
));

// Header component
const Header = memo(() => (
    <header className="relative z-10 pt-6 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
            <img
                src="/logo192.png"
                alt="Cleanify Logo"
                className="h-[50px] w-auto"
                draggable="false"
            />

            <AppStoreButton
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-5 rounded-xl transition-all shadow-md hover:shadow-lg text-sm md:text-base"/>
        </div>
    </header>
));

// App Store Button component
const AppStoreButton = memo(({className}: { className?: string }) => (
    <a
        href="https://apps.apple.com/app/id1631533361"
        target="_blank"
        rel="noopener noreferrer"
        className={className}
    >
        Download Now
    </a>
));

// Feature item component
const FeatureItemComponent = memo(
    ({
         icon,
         text,
         index,
     }: {
        icon: React.ReactNode;
        text: string;
        index: number;
    }) => (
        <motion.div
            key={index}
            className="flex items-center gap-4 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm p-3 rounded-xl shadow-sm"
            initial={{opacity: 0, x: -20}}
            animate={{opacity: 1, x: 0}}
            transition={{delay: 0.3 + index * 0.1}}
        >
            <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">{icon}</div>
            <span className="text-gray-800 dark:text-gray-200 font-medium">
        {text}
      </span>
        </motion.div>
    ),
);

// Star rating component
const StarRating = memo(() => (
    <div className="flex">
        {Array(5)
            .fill(0)
            .map((_, i) => (
                <svg
                    key={i}
                    className="text-yellow-400 w-6 h-6"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
            ))}
    </div>
));

// Main content section
const ContentSection = memo(
    ({features}: { features: FeatureItemProps[] }) => (
        <motion.div
            initial={{opacity: 0, x: -20}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8, ease: "easeOut"}}
            className="max-w-xl w-full"
        >
            <motion.h1
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.3}}
                className="font-bold text-4xl md:text-5xl lg:text-6xl mb-6 text-gray-900 dark:text-white leading-tight"
            >
        <span
            className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
          Clean & Optimize
        </span>
                <br/>
                Your iPhone
            </motion.h1>

            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                Cleanify is the most powerful and intuitive iOS cleaner app that helps
                you maintain your device at peak performance.
            </p>

            <div className="space-y-5 mb-8">
                {features.map((feature, index) => (
                    <FeatureItemComponent
                        key={index}
                        icon={feature.icon}
                        text={feature.text}
                        index={index}
                    />
                ))}
            </div>

            <div className="flex justify-start mb-8">
                <motion.a
                    href="https://apps.apple.com/app/id1631533361"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative"
                    whileHover={{scale: 1.05}}
                    initial={{scale: 1}}
                >
                    <img
                        src="/appstore.svg"
                        alt="Download on the App Store"
                        className="h-[60px] md:h-[70px] shadow-md rounded-lg"
                        draggable="false"
                    />
                </motion.a>
            </div>

            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.3, duration: 0.6}}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
            >
                <div className="flex items-center gap-4">
                    <StarRating/>
                    <div className="flex flex-col">
            <span className="text-gray-800 dark:text-white font-semibold">
              4.9 out of 5
            </span>
                        <span className="text-gray-600 dark:text-gray-400 text-sm">
              Over 50,000 satisfied users
            </span>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    ),
);

// Device mockup section
const DeviceMockup = memo(() => (
    <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.6, duration: 0.8}}
        className="w-full max-w-sm md:max-w-md relative"
    >
        <div className="relative w-full overflow-hidden ">
            <img
                src="/promo.png"
                alt="Cleanify Interface"
                className="w-full h-auto object-contain relative z-0"
                draggable="false"
            />
        </div>

        {/* Stats elements */}
        <StatElement
            position="-left-8 top-1/4"
            icon={<FaImages className="text-blue-600 dark:text-blue-400 w-4 h-4"/>}
            value="65.3 GB"
            label="Space cleaned"
            delay={0.9}
            animateFrom="left"
        />

        <StatElement
            position="-right-4 top-2/3"
            icon={
                <FaBatteryFull className="text-green-600 dark:text-green-400 w-4 h-4"/>
            }
            value="+45%"
            label="Battery life"
            delay={1.1}
            animateFrom="right"
        />
    </motion.div>
));

// Stat element component
const StatElement = memo(
    ({
         position,
         icon,
         value,
         label,
         delay,
         animateFrom,
     }: {
        position: string;
        icon: React.ReactNode;
        value: string;
        label: string;
        delay: number;
        animateFrom: "left" | "right";
    }) => (
        <motion.div
            className={`absolute ${position} bg-white dark:bg-gray-800 shadow-lg rounded-xl p-3 z-20`}
            initial={{opacity: 0, x: animateFrom === "left" ? -20 : 20}}
            animate={{opacity: 1, x: 0}}
            transition={{delay}}
        >
            <div className="flex items-center gap-2">
                <div
                    className={`bg-${animateFrom === "left" ? "blue" : "green"}-100 dark:bg-${animateFrom === "left" ? "blue" : "green"}-900 p-2 rounded-lg`}
                >
                    {icon}
                </div>
                <div className="text-sm">
                    <div className="font-semibold text-gray-900 dark:text-white">
                        {value}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                        {label}
                    </div>
                </div>
            </div>
        </motion.div>
    ),
);

// Footer component
const Footer = memo(() => (
    <footer
        className="py-6 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-gray-600 dark:text-gray-400 mb-2">
                &copy; {new Date().getFullYear()} Cleanify. All rights reserved.
            </p>
            <div className="flex justify-center space-x-4 text-sm text-gray-500 dark:text-gray-500">
                <a
                    href="/privacy-policy"
                    className="hover:text-blue-500 transition-colors"
                >
                    Privacy Policy
                </a>
                <span>•</span>
                <a
                    href="/terms-of-service"
                    className="hover:text-blue-500 transition-colors"
                >
                    Terms of Service
                </a>
            </div>
        </div>
    </footer>
));

export default memo(Hero);
